import { useState, useEffect, useCallback } from 'react';
import { Shield, Download, CheckCircle, AlertTriangle, Clock, Users, Building2, UserX, Lock, ChevronDown, ChevronRight, Search, X, HelpCircle } from 'lucide-react';
import useAuthStore from '../stores/authStore';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import accessReviewTutorial from '../components/tutorials/configs/accessReviewTutorial';

const TOKEN = 'authToken';

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

async function api(path, options = {}) {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  return res.json();
}

const STALE_COLORS = {
  active: { bg: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', label: 'Active' },
  warning: { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', label: '90+ Days' },
  critical: { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', label: '180+ Days' },
  never_logged_in: { bg: 'rgba(107, 114, 128, 0.15)', color: '#6b7280', label: 'Never' },
};

function StaleBadge({ status }) {
  const s = STALE_COLORS[status] || STALE_COLORS.active;
  return (
    <span style={{ background: s.bg, color: s.color, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
      {s.label}
    </span>
  );
}

function PermBadge({ label, active }) {
  if (!active) return null;
  return (
    <span style={{ background: 'rgba(0, 212, 170, 0.15)', color: 'var(--accent)', padding: '2px 6px', borderRadius: 8, fontSize: 10, fontWeight: 500, marginRight: 4 }}>
      {label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div style={{ background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <Icon size={20} style={{ color }} />
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</div>
      </div>
    </div>
  );
}

function TenantSection({ tenant, onCertify }) {
  const [expanded, setExpanded] = useState(true);
  const [search, setSearch] = useState('');

  const filtered = tenant.users.filter(u =>
    !search || u.email.toLowerCase().includes(search.toLowerCase()) || (u.name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', cursor: 'pointer', background: 'var(--bg-surface)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Building2 size={16} style={{ color: 'var(--accent)' }} />
          <span style={{ fontWeight: 600, fontSize: 15 }}>{tenant.tenantName}</span>
          <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>{tenant.userCount} users</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {tenant.staleCount > 0 && (
            <span style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
              {tenant.staleCount} stale
            </span>
          )}
          {tenant.lockedCount > 0 && (
            <span style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
              {tenant.lockedCount} locked
            </span>
          )}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '0 20px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0', position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, color: 'var(--text-tertiary)' }} />
            <input
              type="text"
              placeholder="Filter users..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ background: 'var(--bg-page)', border: '0.5px solid var(--border)', borderRadius: 6, padding: '6px 10px 6px 30px', color: 'var(--text-primary)', fontSize: 13, width: 220 }}
            />
            {search && <X size={14} style={{ cursor: 'pointer', color: 'var(--text-tertiary)' }} onClick={() => setSearch('')} />}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['User', 'Role', 'Status', 'Permissions', 'Integrations', 'Last Login', 'Stale', 'Locked'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-secondary)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.userId} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 10px' }}>
                      <div style={{ fontWeight: 500 }}>{u.name || '-'}</div>
                      <div style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>{u.email}</div>
                    </td>
                    <td style={{ padding: '10px 10px' }}>
                      <span style={{
                        background: u.role === 'ADMIN' ? 'rgba(59,130,246,0.15)' : u.role === 'OPERATOR' ? 'rgba(139,92,246,0.15)' : 'rgba(107,114,128,0.15)',
                        color: u.role === 'ADMIN' ? '#3b82f6' : u.role === 'OPERATOR' ? '#8b5cf6' : '#6b7280',
                        padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600
                      }}>
                        {u.role}
                      </span>
                      {u.isPlatformAdmin && (
                        <span style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', padding: '2px 6px', borderRadius: 8, fontSize: 10, fontWeight: 500, marginLeft: 4 }}>
                          PLATFORM
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 10px' }}>
                      <span style={{
                        color: u.status === 'ACTIVE' ? '#22c55e' : u.status === 'PENDING' ? '#f59e0b' : '#ef4444',
                        fontSize: 12
                      }}>
                        {u.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 10px' }}>
                      <PermBadge label="Lockdown" active={u.permissions.canLockdown} />
                      <PermBadge label="Intercom" active={u.permissions.canAnswerIntercom} />
                      <PermBadge label="Shabbat" active={u.permissions.canActivateShabbat} />
                    </td>
                    <td style={{ padding: '10px 10px' }}>
                      {u.integrationLinks.waveVms && <span style={{ color: '#3b82f6', fontSize: 11, marginRight: 6 }}>Wave</span>}
                      {u.integrationLinks.feenics && <span style={{ color: '#10b981', fontSize: 11 }}>Feenics</span>}
                      {!u.integrationLinks.waveVms && !u.integrationLinks.feenics && <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>None</span>}
                    </td>
                    <td style={{ padding: '10px 10px', fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never'}
                      {u.daysSinceLogin !== null && <span style={{ color: 'var(--text-tertiary)', marginLeft: 4 }}>({u.daysSinceLogin}d)</span>}
                    </td>
                    <td style={{ padding: '10px 10px' }}><StaleBadge status={u.staleStatus} /></td>
                    <td style={{ padding: '10px 10px' }}>
                      {u.isLocked ? <Lock size={14} style={{ color: '#ef4444' }} /> : <span style={{ color: 'var(--text-tertiary)' }}>-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AccessReview() {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(accessReviewTutorial);
  const { user } = useAuthStore();
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [certifying, setCertifying] = useState(false);
  const [certNotes, setCertNotes] = useState('');
  const [showCertForm, setShowCertForm] = useState(false);
  const [tab, setTab] = useState('report');

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/api/access-review/platform');
      if (data.success !== false) setReport(data);
      const hist = await api('/api/access-review/history');
      if (hist.records) setHistory(hist.records);
    } catch (err) {
      console.error('Failed to load access review', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadReport(); }, [loadReport]);

  const handleExportCSV = () => {
    window.location.href = '/api/access-review/platform/csv';
  };

  const handleCertify = async () => {
    if (!report) return;
    setCertifying(true);
    try {
      const result = await api('/api/access-review/certify', {
        method: 'POST',
        body: JSON.stringify({
          scope: 'platform',
          actions: {
            confirmed: report.summary.totalUsers,
            modified: 0,
            revoked: 0,
          },
          notes: certNotes || 'Quarterly access review completed. All access confirmed appropriate.',
        }),
      });
      if (result.success) {
        setShowCertForm(false);
        setCertNotes('');
        loadReport();
        alert('Access review certification recorded successfully.');
      }
    } catch (err) {
      console.error('Certification failed', err);
    }
    setCertifying(false);
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
        <div className="spinner" style={{ margin: '0 auto 16px' }} />
        Loading access review report...
      </div>
    );
  }

  if (!report) {
    return <div style={{ padding: 40, color: '#ef4444' }}>Failed to load access review report.</div>;
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Shield size={22} style={{ color: 'var(--accent)' }} />
            Access Review
          </h1>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: '4px 0 0' }}>
            Quarterly user access certification and compliance reporting
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="tutorial-help-btn" onClick={() => startTutorial(accessReviewTutorial.module, accessReviewTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
          <button onClick={handleExportCSV} className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={14} /> Export CSV
          </button>
          <button onClick={() => setShowCertForm(!showCertForm)} className="btn btn-primary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#00d4aa', color: '#0a0a0f' }}>
            <CheckCircle size={14} /> Certify Review
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 0 }}>
        {[{ id: 'report', label: 'Access Report' }, { id: 'history', label: 'Certification History' }].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              background: 'none', border: 'none', color: tab === t.id ? '#00d4aa' : '#64748b',
              fontWeight: tab === t.id ? 600 : 400, fontSize: 14, padding: '8px 16px', cursor: 'pointer',
              borderBottom: tab === t.id ? '2px solid #00d4aa' : '2px solid transparent', marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Certification Form */}
      {showCertForm && (
        <div style={{ background: 'rgba(0,212,170,0.05)', border: '1px solid rgba(0,212,170,0.2)', borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 12px', color: 'var(--accent)' }}>Record Access Review Certification</h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
            By certifying, you attest that you have reviewed all user access rights and confirm they are appropriate for each user's current role and responsibilities.
          </p>
          <textarea
            placeholder="Notes (optional) -- e.g., 'All access confirmed. Removed VMS link for user X.'"
            value={certNotes}
            onChange={e => setCertNotes(e.target.value)}
            style={{ width: '100%', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: 10, color: 'var(--text-primary)', fontSize: 13, minHeight: 60, resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={handleCertify} disabled={certifying} className="btn btn-primary btn-sm" style={{ background: '#00d4aa', color: '#0a0a0f' }}>
              {certifying ? 'Recording...' : 'Confirm Certification'}
            </button>
            <button onClick={() => setShowCertForm(false)} className="btn btn-secondary btn-sm">Cancel</button>
          </div>
        </div>
      )}

      {tab === 'report' && (
        <>
          {/* Summary Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
            <StatCard icon={Building2} label="Sites" value={report.summary.totalTenants} color="#00d4aa" />
            <StatCard icon={Users} label="Total Users" value={report.summary.totalUsers} color="#3b82f6" />
            <StatCard icon={AlertTriangle} label="Stale Accounts" value={report.summary.totalStale} color={report.summary.totalStale > 0 ? '#f59e0b' : '#22c55e'} />
            <StatCard icon={Lock} label="Locked" value={report.summary.totalLocked} color={report.summary.totalLocked > 0 ? '#ef4444' : '#22c55e'} />
            <StatCard icon={UserX} label="Orphaned" value={report.summary.orphanedCount} color={report.summary.orphanedCount > 0 ? '#f59e0b' : '#22c55e'} />
          </div>

          {/* Tenant Sections */}
          {report.tenants.map(t => (
            <TenantSection key={t.tenantId} tenant={t} />
          ))}

          {/* Orphaned Users */}
          {report.orphanedUsers.length > 0 && (
            <div style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, padding: 20, marginTop: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 12px', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 8 }}>
                <UserX size={16} /> Orphaned Users (No Tenant Access)
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Email', 'Name', 'Status', 'Platform Admin', 'Last Login', 'Created'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-secondary)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.orphanedUsers.map(u => (
                    <tr key={u.userId} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 10px' }}>{u.email}</td>
                      <td style={{ padding: '8px 10px' }}>{u.name || '-'}</td>
                      <td style={{ padding: '8px 10px', color: u.status === 'ACTIVE' ? '#22c55e' : '#f59e0b' }}>{u.status}</td>
                      <td style={{ padding: '8px 10px' }}>{u.isPlatformAdmin ? 'Yes' : 'No'}</td>
                      <td style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-secondary)' }}>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never'}</td>
                      <td style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-secondary)' }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'history' && (
        <div>
          {history.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}>
              <Clock size={32} style={{ marginBottom: 12, opacity: 0.5 }} />
              <div>No certifications recorded yet. Complete your first review above.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {history.map(h => (
                <div key={h.id} style={{ background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 8, padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <CheckCircle size={16} style={{ color: '#22c55e' }} />
                      <span style={{ fontWeight: 600, fontSize: 14 }}>Access Review Certified</span>
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{new Date(h.createdAt).toLocaleString()}</span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{h.description}</div>
                  {h.metadata?.notes && (
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6, fontStyle: 'italic' }}>Notes: {h.metadata.notes}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showPrompt && (
        <TutorialPrompt
          module={accessReviewTutorial.module}
          moduleName={accessReviewTutorial.moduleName}
          steps={accessReviewTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
}
